import React, { useEffect, useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { RunRow, executionStatsService, isAbort } from '../../services/executionStatsService';
import { FLOW_ATTRIBUTION, relativeTime } from './executionStatsVocabulary';
import { rangeBounds, rangeFor } from './executionStatsWindows';
import { useWorkflowRunStats } from './useExecutionSummary';
import { dsVar } from '../../theme/tokens';

/** How far back to look for a success: the longest range the run log serves. */
const LOOKBACK = rangeFor('12m');

const muted = { fontSize: '0.75rem', color: dsVar('fg-muted') };

const absoluteTime = (iso: string) =>
    new Date(iso).toLocaleString(undefined, {
        day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });

/**
 * "Last successful run 2h ago", for a workflow's editor header.
 *
 * The run counts belong on a dashboard; while editing, the question is whether
 * this workflow last worked, and when. Same attribution as the workflow card:
 * the campaign's own runs, else every run of the flow.
 */
const LastSuccessfulRun: React.FC<{ campaignId?: string | null; flowCode?: string | null }> = ({
    campaignId,
    flowCode
}) => {
    const stats = useWorkflowRunStats({ campaignId, flowCode });
    const { attribution } = stats;
    const key = attribution === 'campaign' ? campaignId : attribution === 'flow' ? flowCode : null;

    // undefined while loading, null when there is no success in the lookback.
    const [run, setRun] = useState<RunRow | null | undefined>(undefined);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        setRun(undefined);
        setFailed(false);
        if (!attribution || !key) return;
        const controller = new AbortController();
        executionStatsService
            .listRuns(
                {
                    scope: 'flow',
                    keyType: attribution === 'campaign' ? 'campaign' : 'code',
                    keys: [key],
                    groupVersions: attribution === 'flow',
                    ...rangeBounds(LOOKBACK),
                    bucket: 'success',
                    page: 1,
                    limit: 1
                },
                { signal: controller.signal }
            )
            .then((res) => setRun(res.runs[0] ?? null))
            .catch((err) => { if (!isAbort(err)) setFailed(true); });
        return () => controller.abort();
    }, [attribution, key]);

    if (stats.error || failed) {
        return <Typography component="span" sx={muted}>Run history unavailable</Typography>;
    }
    if (stats.loading || run === undefined) {
        return <Typography component="span" sx={muted}>Checking runs…</Typography>;
    }

    const at = run?.completedAt ?? run?.startedAt ?? null;
    if (!run || !at) {
        return (
            <Typography component="span" sx={{ ...muted, color: dsVar('fg-2') }}>
                No successful run in the last 12 months
            </Typography>
        );
    }

    return (
        <Tooltip
            arrow
            placement="bottom"
            title={
                <Box sx={{ p: 0.5 }}>
                    <Typography sx={{ fontSize: '0.6875rem' }}>{absoluteTime(at)}</Typography>
                    {run.name && <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-soft') }}>{run.name}</Typography>}
                    {attribution === 'flow' && (
                        <Typography sx={{ fontSize: '0.6875rem', mt: 0.5, fontStyle: 'italic' }}>{FLOW_ATTRIBUTION}</Typography>
                    )}
                </Box>
            }
        >
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: '6px', cursor: 'help' }}>
                <Typography component="span" sx={{ fontSize: '0.75rem', color: dsVar('fg-2') }}>Last successful run</Typography>
                <Typography component="span" sx={{ fontSize: '0.75rem', color: dsVar('fg-soft'), fontWeight: 600 }}>
                    {relativeTime(at)}
                </Typography>
            </Box>
        </Tooltip>
    );
};

export default LastSuccessfulRun;
